<template>
  <modal-card
    :title="getTitle"
    fixed
    closeButton
    light
    @close="$emit('cancel')"
  >
    <div class="d-flex pa-6 pt-4 pb-4 fields-filters">
      <search-toggle-field
        v-model="search"
        @input="loadSuggestions"
        placeholder="Search"
        alwaysOpen
        autofocus
        large
        dark
        :delay="500"
      />
    </div>

    <div v-if="suggestionsLoading" class="spinner-wrap">
      <spinner />
    </div>

    <div class="fields-list-wrap" v-else>
      <div class="pa-6" v-if="!filteredSuggestions.length">
        <p class="font-weight-bold title text-center mb-0">No options found.</p>
      </div>

      <field-block
        v-if="search && showAddOptionsBlock"
        class="pl-4 pr-4 mt-4 mb-3"
        title="Add New"
      >
        <div class="fields-list__item new-option" @click="addOption(search)">
          <div class="fields-list__item-text">
            {{ search }}
          </div>
          <v-icon color="appMain">add</v-icon>
        </div>
      </field-block>

      <ul class="fields-list" v-if="filteredSuggestions.length">
        <template v-for="(suggestion, idx) in filteredSuggestions">
          <li v-if="suggestion.header" class="fields-list__item" :key="idx">
            <strong class="appMain--text">{{ suggestion.header }}</strong>
          </li>
          <li
            v-else
            class="fields-list__item"
            :key="idx"
            @click="selectField(suggestion.value || suggestion)"
            :class="{ active: isFieldSelected(suggestion.value || suggestion) }"
          >
            <div class="fields-list__item-text">
              <avatar
                v-if="isUserListField"
                :img="suggestion.photo"
                size="small"
                class="mr-2"
              />
              {{ suggestion.text ? suggestion.text : suggestion }}
            </div>
            <v-icon
              color="appMain"
              v-if="isFieldSelected(suggestion.value || suggestion)"
              >check_circle</v-icon
            >
          </li>
        </template>
      </ul>
    </div>

    <v-btn color="appMain" slot="actions" @click="selectAndExit">Select</v-btn>
  </modal-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import SearchToggleField from '@/components/basic/search-toggle-field/SearchToggleField';
import Avatar from '@/components/basic/avatar/Avatar';

import clone from 'lodash/clone';
import capitalize from 'lodash/capitalize';

export default {
  name: 'GroupFieldSelectValue',
  components: {
    Avatar,
    SearchToggleField,
  },
  props: {
    filter: {},
    filterEvent: {},
    filtersGroup: {},
    meta: {},
    teamFilterOff: Boolean,
  },
  data() {
    return {
      search: '',
      currentValue: [],
      suggestions: [],
      namesSuggestionsTypes: {
        campaign: 'campaigns',
        campaigns: 'campaigns',
        lesson: 'lessons',
        trigger: 'triggers',
      },
    };
  },
  computed: {
    ...mapGetters(['suggestionsLoading']),
    ...mapGetters('users', ['usersOptions']),
    showAddOptionsBlock() {
      const type = this.filter?.field?.type;

      const disabledForTypes = [
        'relation',
        'related_contacts',
        'related_custom_object',
        'user_list',
      ];

      if (disabledForTypes.includes(type)) {
        return false;
      }

      const disableNewOptionsFor = [
        'lesson',
        'trigger',
        'campaign',
        'campaigns',
        'lesson_item',
      ];
      const alreadyExists = this.filteredSuggestions.some((i) => {
        let name = (i.text || i.header || i || '').toLowerCase();
        return name === this.search.toLowerCase();
      });
      const currentField = this.filterField?.field || this.filterField;

      return (
        !disableNewOptionsFor.some((i) => i === currentField) && !alreadyExists
      );
    },

    lessonEventWithMeta() {
      const eventsWithMeta = [
        'answered_question',
        'watched_video',
        'shared',
        'uploaded_video',
      ];
      return eventsWithMeta.includes(this.filterEvent);
    },

    getTitle() {
      if (this.filterField)
        return `Select ${capitalize(this.filterField.text)}`;
      return 'Select Field Value';
    },

    isSpecialField() {
      if (!this.filter.field) return false;
      let specialTypes = Object.keys(this.namesSuggestionsTypes);
      return specialTypes.includes(this.filter.field.field);
    },

    isAnsweredEventFields() {
      if (!this.filter.field) return false;
      return ['item', 'answer'].includes(this.filter.field.field);
    },

    isUserListField() {
      return this.filter.field?.type === 'user_list';
    },

    lessonId() {
      return this.getFieldValue('lesson');
    },

    lessonItemId() {
      return this.getFieldValue('item');
    },

    filterField() {
      if (!this.filter || !this.filter.field) return {};
      return this.filter.field;
    },

    filteredSuggestions() {
      let suggestionsArr = this.addCurrentValueToSuggestions(this.suggestions);
      return suggestionsArr.filter((i) => {
        let name = i.text || i.header || i;
        return name && name.toLowerCase().includes(this.search.toLowerCase());
      });
    },
  },
  methods: {
    ...mapActions([
      'loadContactSuggestions',
      'loadContactNameSuggestions',
      'loadActivitySuggestions',
      'loadLessonMeta',
    ]),
    ...mapActions('users', ['loadUsers']),

    // extract field value from an array of fields
    getFieldValue(f) {
      let fieldObj = this.filtersGroup.find(({ field }) => {
        return field && field.field === f;
      });
      return fieldObj ? fieldObj.value.value || fieldObj.value : '';
    },

    isFieldSelected(suggestion) {
      if (Array.isArray(this.currentValue)) {
        return this.currentValue.some(
          (i) => i === suggestion || i.value === suggestion
        );
      } else {
        return (
          this.currentValue === suggestion ||
          this.currentValue.value === suggestion
        );
      }
    },

    addCurrentValueToSuggestions(arr) {
      const isSuggestionExist = (val) => {
        const suggestion = val.value || val;
        return arr.some((i) => i === suggestion || i.value === suggestion);
      };
      const isArrayOfObjects = arr.some((i) => i.value);
      const prepareOption = (val) => {
        return isArrayOfObjects && !val.value ? { value: val, text: val } : val;
      };
      const addToArray = (val) => {
        if (!val || isSuggestionExist(val)) return;
        arr.unshift(prepareOption(val));
      };

      if (Array.isArray(this.currentValue)) {
        this.currentValue.forEach((i) => addToArray(i));
      } else {
        addToArray(this.currentValue);
      }

      return arr;
    },

    addOption(value) {
      this.selectField(value);
      this.search = '';
      this.loadSuggestions('');
    },

    selectField(field) {
      let isSelected = this.isFieldSelected(field);

      if (Array.isArray(this.currentValue)) {
        if (isSelected) {
          this.currentValue = this.currentValue.filter((i) => {
            return i.value ? i.value !== field : i !== field;
          });
        } else {
          // only one value allowed for campaign filter for activities
          // both descendant operators resolve a single root contact server-side
          if (
            ['$descendantOf', '$notDescendantOf'].includes(
              this.filter?.operator?.value
            )
          ) {
            this.currentValue = [field];
          } else {
            this.currentValue.push(field);
          }
        }
      } else {
        this.currentValue = isSelected ? '' : field;
      }
    },

    setSuggestionsFromLessonMeta() {
      if (!this.lessonId) return;

      this.loadLessonMeta(this.lessonId).then((items = []) => {
        if (this.filterField.field === 'answer') {
          const lessonItem = items.find((i) => i.uuid === this.lessonItemId);
          if (lessonItem && lessonItem.event_properties.answer) {
            const answer = lessonItem.event_properties.answer;
            if (answer.values) {
              this.suggestions = (answer.values || []).map((i, idx) => {
                if (typeof i === 'object')
                  return { text: i.label, value: i.value + '' };
                return { text: i, value: idx + '' };
              });
            }

            if (answer.range) {
              let range = answer.range;
              range = Array.from(
                { length: range[range.length - 1] },
                (v, i) => i + 1
              );
              this.suggestions = range.map((i) => {
                return { text: `${i} Checked`, value: i + '' };
              });
            }
          }
        }

        if (this.filterField.field === 'item') {
          this.suggestions = items
            .filter((i) => {
              if (this.lessonEventWithMeta)
                return i.event_name === this.filterEvent;
              return !!i.event_name;
            })
            .map((i) => {
              return { text: i.summary || i.type, value: i.uuid };
            });
        }
      });
    },

    loadSuggestions(query) {
      // if filter field has options - use options as suggestions
      if (
        Array.isArray(this.filterField.options) &&
        this.filterField.options.length
      ) {
        return (this.suggestions = this.filterField.options);
      }

      // special suggestions for lesson item | answer
      if (this.isAnsweredEventFields) {
        return this.setSuggestionsFromLessonMeta();
      }

      // special suggestions for lesson|trigger|campaign
      if (this.isSpecialField) {
        if (this.suggestions.length) return;

        let query = [];
        if (this.lessonEventWithMeta) {
          query.push(`query=event:${this.filterEvent}`);
        }

        const campaignId = this.meta?.campaign?.id;
        if (campaignId) {
          query.push(`campaign=${campaignId}`);
        }

        query = query.join('&');

        let type = this.namesSuggestionsTypes[this.filter.field.field];
        this.loadActivitySuggestions({ type, query }).then((suggestions) => {
          this.suggestions = suggestions;
        });
        return;
      }

      if (this.filter.field?.type === 'user_list') {
        return this.loadUsers().then(() => {
          this.suggestions = this.usersOptions.map((u) => ({
            text: u.label,
            value: u.value,
            photo: u.photo,
          }));
        });
      }

      if (this.filter.field?.type === 'related_contacts') {
        return this.loadRelatedContacts(query);
      }

      let params = {
        field: this.filter.field.value,
        query: query || '',
        created_after: '',
        teamFilterOff: this.teamFilterOff,
      };

      this.loadContactSuggestions(params).then((suggestions) => {
        this.suggestions = suggestions;
      });
    },

    async loadRelatedContacts(query) {
      let injectSuggestion = (newSuggestion) => {
        let found = this.suggestions.find((suggestion) => {
          return suggestion.value === newSuggestion.value;
        });

        if (!found) {
          this.suggestions.push(newSuggestion);
        }
      };

      let suggestions = await this.loadContactNameSuggestions({ query });
      suggestions.forEach(injectSuggestion);

      //for initial loading of suggestions..
      if (query) return;

      let found = this.suggestions.find((suggestion) => {
        return suggestion.value === this.currentValue;
      });

      if (found) return;

      suggestions = await this.loadContactNameSuggestions({
        filters: { id: this.currentValue },
      });

      suggestions.forEach(injectSuggestion);
    },

    selectAndExit() {
      if (this.filter.value !== this.currentValue) {
        this.$emit('updateFieldValue', this.filter.field.field);
      }

      this.filter.value = this.currentValue;
      this.suggestions = [];
      this.$emit('close');
    },
  },
  mounted() {
    this.currentValue = clone(this.filter.value);
    this.loadSuggestions('');
  },
};
</script>

<style lang="less">
.fields-list-wrap {
  height: calc(100% - 76px);
  overflow: auto;

  .field-block + .fields-list {
    border-top: 1px solid #eee;

    .fields-list__item:first-child {
      border-top: 2px solid #f2f2f3;
    }
  }
}

.fields-list {
  padding: 12px 16px !important;

  &__item {
    display: flex;
    height: 56px;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    cursor: pointer;

    &.active {
      background: #f2f2f3;
    }

    &:not(:last-child) {
      border-bottom: 2px solid #f2f2f3;
    }

    &-text {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

.fields-filters {
  box-shadow: @content-shadow;
}
</style>

<style scoped>
.spinner-wrap {
  height: calc(100% - 68px);
}

.new-option {
  background-color: var(--v-lightGray-base);
}
</style>
